<template>
  <div
    class="vqb-group"
    :class="classObject"
  >
    <div
      class="vqb-group-heading"
      :class="{ 'panel-heading': styled }"
    >
      <div
        class="match-type-container"
        :class="{ 'form-inline': styled }"
      >
        <template v-if="showOnly">
          <label :for="matchTypeId">{{ labels.matchTypeStart }}</label> {{ query.operator === 'and' ? labels.matchTypeAll : labels.matchTypeAny }}
          <label :for="matchTypeId">{{ labels.matchTypeEnd }}</label>
        </template>
        <template v-else>
          <div :class="{ 'form-group': styled }">
            <label :for="matchTypeId">{{ labels.matchTypeStart }}</label>
            <select
              :id="matchTypeId"
              v-model="query.operator"
              :class="{ 'form-control': styled }"
            >
              <option value="and">
                {{ labels.matchTypeAll }}
              </option>
              <option value="or">
                {{ labels.matchTypeAny }}
              </option>
            </select>
            <label :for="matchTypeId">{{ labels.matchTypeEnd }}</label>
          </div>
          <button
            v-if="canRemove && !showOnly"
            type="button"
            :class="{ 'close pull-right': styled }"
            @click="remove"
            v-safe-html="labels.removeGroup"
          />
        </template>
      </div>
    </div>

    <div
      class="vqb-group-body"
      :class="{ 'panel-body': styled }"
    >
      <div class="children">
        <!-- Either a query-builder-group or a query-builder-rule
                     That is why all properties must be loaded, we don't know before hand if it is a group or a rule.
                -->
        <component
          :is="getType(child)"
          v-for="(child, index) in query.value"
          :key="index"
          :type="getType(child)"
          :query.sync="child.query"
          :rule-types="ruleTypes"
          :rules="rules"
          :rule="ruleById(child.query.field)"
          :index="index"
          :max-depth="maxDepth"
          :depth="depth + 1"
          :styled="styled"
          :labels="labels"
          :show-only="showOnly"
          :program-id="programId"
          @child-deletion-requested="removeChild"
        />
      </div>

      <div
        class="rule-actions"
        :class="{ 'form-inline': styled }"
      >
        <div class="col-md-11 col-sm-11">
          <template v-if="showButtons">
            <select
              v-model="selectedRule"
              :class="{ 'form-control': styled }"
            >
              <option
                v-for="(rule, index) in rules"
                :key="index"
                :value="rule"
              >
                {{ trans('filters.' +
                  rule.label) }}
              </option>
            </select>

            <button
              type="button"
              :class="{ 'btn btn-default btn-sm': styled }"
              @click="addRule"
              v-safe-html="labels.addRule"
            />

            <div
              class="btn-group pull-right"
              role="group"
            >
              <button
                v-if="this.depth < this.maxDepth"
                type="button"
                :class="{ 'btn btn-default btn-sm': styled }"
                @click="addGroup"
                v-safe-html="labels.addGroup"
              />
              <button
                v-if="this.depth < this.maxDepth"
                type="button"
                :class="{ 'btn btn-default btn-sm': styled }"
                @click="addNot"
                v-safe-html="labels.addNot"
              />
            </div>
          </template>
        </div>
        <div
          v-if="!showOnly"
          class="col-md-1"
        >
          <span
            type="button"
            :class="{ 'fa fa-chevron-up pull-right close' : showAdd, 'fa fa-chevron-down pull-right close' : !showAdd }"
            @click="showAdd = !showAdd"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QueryBuilderRule from './QueryBuilderRule.vue'
import QueryBuilderNot from './QueryBuilderNot.vue'

import { deepClone } from './utilities.js'

export default {
  name: 'QueryBuilderGroup',

  components: {
    QueryBuilderRule,
    QueryBuilderNot,
  },

  props: ['ruleTypes', 'type', 'query', 'rules', 'index', 'maxDepth', 'depth', 'styled', 'labels', 'showOnly', 'programId'],

  data () {
    return {
      selectedRule: this.rules[0],
      showAdd: true,
    }
  },

  computed: {
    matchTypeId () {
      // We want the respective id's of the group-select and/or to be the same!

      return 'vqb-match-type-' + this.depth + '-' + this.index
    },
    classObject () {
      var classObject = {
        'panel ': this.styled,
      }

      classObject['depth-' + this.depth.toString()] = this.styled

      return classObject
    },

    showButtons () {
      return !this.showOnly && this.showAdd
    },

    canRemove () {
      // determines wheter this group element can be removed

      if (this.depth <= 1) {
        // if the group is the root element, we can't remove it!
        return false
      }

      if (this.$parent.type === 'query-builder-not') {
        // if the parent is a not-operator, we can't remove the group element
        return false
      }

      return true
    },
  },

  methods: {
    getType (child) {
      // get the component type of the new child, we don't want to store it in the query anymore
      // the return value is either query-builder-rule or query-builder-group or query-builder-not

      if (_.has(child.query, 'field')) {
        return 'query-builder-rule'
      } else if (_.has(child.query, 'operator') && child.query.operator === 'not') {
        return 'query-builder-not'
      } else {
        return 'query-builder-group'
      }
    },

    ruleById (ruleId) {
      // retrieve the selected rule type by using its id (e.g. datum, text)
      var rule = null

      this.rules.forEach(function (value) {
        if (value.id === ruleId) {
          rule = value

          return false
        }
      })

      return rule
    },

    addRule () {
      let updated_query = deepClone(this.query)

      let child = {
        query: {
          field: this.selectedRule.id,
          operator: this.selectedRule.operators[0],
          value: null,
        },
      }

      // A bit hacky, but `v-model` on `select` requires an array.
      //      if (this.ruleById(child.query.rule).type === 'multi-select') {
      //        child.query.value = [];
      //      }

      updated_query.value.push(child)
      this.$emit('update:query', updated_query)
    },

    addGroup () {
      let updated_query = deepClone(this.query)

      if (this.depth < this.maxDepth) {
        updated_query.value.push({
          query: {
            operator: 'and',
            value: [],
          },
        })
        this.$emit('update:query', updated_query)
      }
    },

    addNot () {
      let updated_query = deepClone(this.query)

      if (this.depth < this.maxDepth) {
        updated_query.value.push({
          query: {
            operator: 'not',
            value: {
              query: {
                operator: 'and',
                value: [],
              },
            },

          },
        })
        this.$emit('update:query', updated_query)
      }
    },

    remove () {
      this.$emit('child-deletion-requested', this.index)
    },

    removeChild (index) {
      let updated_query = deepClone(this.query)
      updated_query.value.splice(index, 1)
      this.$emit('update:query', updated_query)
    },
  },
}
</script>
